import React, { useContext, useEffect, useState } from "react";
import { Link, useLocation, useNavigate } from "react-router-dom";
import AuthContext from "../context/AuthContext";
import BottomNav from "./BottomNav";
import "./AdminLayout.css";
import axios from "axios";

import {
  FiAward,
  FiBell,
  FiBarChart2,
  FiCalendar,
  FiFileText,
  FiGrid,
  FiHome,
  FiImage,
  FiLayers,
  FiList,
  FiLock,
  FiLogOut,
  FiShield,
  FiShuffle,
  FiTable,
  FiUser,
  FiUsers,
} from "react-icons/fi";

const API_URL = process.env.REACT_APP_API_URL;

const navGroupsByRole = {
  admin: [
    {
      title: "Tổng quan",
      items: [{ path: "https://bongbanhadong.com", icon: FiHome, label: "Trang chủ", external: true }],
    },
    {
      title: "Cấu hình giải",
      items: [
        { path: "/seasons", icon: FiAward, label: "Mùa giải" },
        { path: "/levels", icon: FiBarChart2, label: "Quản lý hạng" },
        { path: "/tables", icon: FiTable, label: "Chia bảng thi đấu" },
      ],
    },
    {
      title: "Đội và VĐV",
      items: [
        { path: "/teams", icon: FiShield, label: "Đội" },
        { path: "/players", icon: FiUsers, label: "Vận động viên" },
        { path: "/rosters", icon: FiList, label: "Quản lý đội" },
      ],
    },
    {
      title: "Thi đấu",
      items: [
        { path: "/schedules", icon: FiCalendar, label: "Lịch thi đấu" },
        { path: "/draw", icon: FiShuffle, label: "Bốc thăm" },
        { path: "/tournaments", icon: FiAward, label: "Giải cá nhân" },
        { path: "/individual-matches", icon: FiLayers, label: "Trận cá nhân" },
      ],
    },
    {
      title: "Hệ thống",
      items: [
        { path: "/notifications", icon: FiBell, label: "Thông báo" },
        { path: "/users", icon: FiUser, label: "Quản lý user" },
        { path: "/change-password", icon: FiLock, label: "Đổi mật khẩu" },
        { path: "/news", icon: FiFileText, label: "Quản lý tin tức" },
        { path: "/ads", icon: FiImage, label: "Quảng cáo" },
      ],
    },
  ],
  manager: [
    {
      title: "Tổng quan",
      items: [{ path: "https://bongbanhadong.com", icon: FiHome, label: "Trang chủ", external: true }],
    },
    {
      title: "Đội bóng",
      items: [
        { path: "/teams", icon: FiShield, label: "Đội của tôi" },
        { path: "/players", icon: FiUsers, label: "Vận động viên" },
        { path: "/rosters", icon: FiList, label: "Quản lý đội" },
      ],
    },
    {
      title: "Thi đấu",
      items: [
        { path: "/schedules", icon: FiCalendar, label: "Lịch thi đấu" },
        { path: "/draw", icon: FiShuffle, label: "Bốc thăm" },
      ],
    },
    {
      title: "Tài khoản",
      items: [
        { path: "/notifications", icon: FiBell, label: "Thông báo" },
        { path: "/change-password", icon: FiLock, label: "Đổi mật khẩu" },
      ],
    },
  ],
  bks: [
    {
      title: "Tổng quan",
      items: [{ path: "https://bongbanhadong.com", icon: FiHome, label: "Trang chủ", external: true }],
    },
    {
      title: "Ban kiểm soát",
      items: [{ path: "/players", icon: FiUsers, label: "Vận động viên" }],
    },
    {
      title: "Tài khoản",
      items: [
        { path: "/notifications", icon: FiBell, label: "Thông báo" },
        { path: "/change-password", icon: FiLock, label: "Đổi mật khẩu" },
      ],
    },
  ],
};

const flattenGroups = (groups) => groups.flatMap((group) => group.items);

const AdminLayout = ({ children }) => {
  const { user, logout } = useContext(AuthContext);
  const location = useLocation();
  const navigate = useNavigate();
  const [unreadCount, setUnreadCount] = useState(0);

  const navGroups = navGroupsByRole[user?.role] || navGroupsByRole.manager;
  const menuItems = flattenGroups(navGroups);

  const handleLogout = () => {
    logout();
    navigate("/login");
  };

  useEffect(() => {
    let active = true;
    const fetchUnread = async () => {
      try {
        const res = await axios.get(`${API_URL}/notifications/unread-count`);
        if (active) setUnreadCount(res.data?.total || 0);
      } catch (err) {
        if (active) setUnreadCount(0);
      }
    };
    fetchUnread();
    const timer = setInterval(fetchUnread, 60000);
    return () => {
      active = false;
      clearInterval(timer);
    };
  }, []);

  const isActivePath = (item) => {
    if (item.external) return false;
    if (item.path === "/") {
      return location.pathname === "/";
    }
    return location.pathname.startsWith(item.path);
  };

  const currentItem =
    menuItems.find((item) => isActivePath(item)) || menuItems[0];

  const roleLabel = {
    admin: "Admin",
    manager: "Manager",
    bks: "BKS",
  }[user?.role] || user?.role || "User";

  return (
    <div className="admin-shell">
      <aside className="admin-sidebar" aria-label="Điều hướng admin">
        <div className="admin-brand">
          <div className="admin-brand-mark">
            <img
              src="https://s3.cloudfly.vn/player-images/logo/logo.png"
              alt="BBHD"
            />
          </div>
          <div>
            <p>BBHD Admin</p>
            <span>Quản lý giải đấu</span>
          </div>
        </div>

        <div className="admin-role-card">
          <div>
            <span className="admin-small-label">Quyền truy cập</span>
            <strong>{roleLabel}</strong>
          </div>
          <span className="admin-role-pill">{roleLabel}</span>
        </div>

        <nav className="admin-nav">
          {navGroups.map((group) => (
            <section className="admin-nav-section" key={group.title}>
              <div className="admin-nav-title">{group.title}</div>
              {group.items.map((item) => {
                const Icon = item.icon;
                const active = isActivePath(item);

                return item.external ? (
                    <a
                      key={item.path}
                      href={item.path}
                      className="admin-nav-link"
                    >
                      <Icon />
                      <span>{item.label}</span>
                    </a>
                  ) : (
                    <Link
                      key={item.path}
                      to={item.path}
                      className={`admin-nav-link ${active ? "active" : ""}`}
                    >
                      <Icon />
                      <span>{item.label}</span>
                      {item.path === "/notifications" && unreadCount > 0 && (
                        <span className="admin-nav-badge">{unreadCount}</span>
                      )}
                    </Link>
                  );
              })}
            </section>
          ))}
        </nav>

        <div className="admin-sidebar-footer">
          <div className="admin-user-chip">
            <div className="admin-avatar">
              {(user?.username || user?.display_name || roleLabel)
                .slice(0, 2)
                .toUpperCase()}
            </div>
            <div>
              <strong>{user?.username || user?.display_name || "Tài khoản"}</strong>
              <span>{roleLabel}</span>
            </div>
          </div>
          <button
            type="button"
            className="admin-logout-btn"
            onClick={handleLogout}
            aria-label="Đăng xuất"
            title="Đăng xuất"
          >
            <FiLogOut />
          </button>
        </div>
      </aside>

      <div className="admin-content-shell">
        <header className="admin-topbar">
          <div>
            <div className="admin-breadcrumb">
              <Link to="/">Admin</Link>
              <span>/</span>
              <span>{currentItem?.label || "Dashboard"}</span>
            </div>
            <h1>{currentItem?.label || "Dashboard"}</h1>
          </div>
          <div className="admin-topbar-actions">
            <div className="admin-topbar-role">
              <FiGrid />
              {roleLabel}
            </div>
            <Link to="/notifications" className="admin-topbar-notifications">
              <FiBell />
              {unreadCount > 0 && <span>{unreadCount}</span>}
            </Link>
            <button
              type="button"
              className="admin-topbar-logout"
              onClick={handleLogout}
            >
              <FiLogOut />
              Đăng xuất
            </button>
          </div>
        </header>

        <main className="admin-layout-main">{children}</main>
      </div>

      <BottomNav unreadCount={unreadCount} />
    </div>
  );
};

export default AdminLayout;
